import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import type { DiagnosisRefResponseResultType } from 'types/api/diagnosisResponseType';
import { isEmpty, isNotEmpty, formatTimeToDate } from 'utils/app';

/**
 * 診断のプレビューURLを取得します。
 */
export function getPreviewUrl(diagnosisPreviewCd: string): string {
  return `${config.url.userWeb}/diagnosis/preview/${diagnosisPreviewCd}`;
}

/**
 * 診断のプレビューURLを開きます。
 */
export function openPreviewUrl(diagnosisPreviewCd: string): void {
  window.open(getPreviewUrl(diagnosisPreviewCd), '_blank');
}

/**
 * 診断の公開URLを取得します。
 */
export function getProductionUrl(diagnosisCd: string): string {
  return `${config.url.userWeb}/diagnosis/${diagnosisCd}`;
}

/**
 * 診断の公開URLを開きます。
 */
export function openProductionUrl(diagnosisCd: string): void {
  window.open(getProductionUrl(diagnosisCd), '_blank');
}

/**
 * 診断のLINE公開URLを取得します。
 */
export function getLineProductionUrl(diagnosisCd: string): string {
  return `${config.url.userWeb}/diagnosis/line/${diagnosisCd}`;
}

/**
 * 診断のLINE公開URLを開きます。
 */
export function openLineProductionUrl(diagnosisCd: string): void {
  window.open(getLineProductionUrl(diagnosisCd), '_blank');
}

/**
 * 公開期間の文字列を作成します。。
 * 公開予定日よりも公開日を優先して表示します。
 */
export function formatPublishTerm(props: {
  diagnosisStatusDiv: number;
  publishStartAt: number;
  publishEndAt: number;
  publishedAt: number;
  terminatedAt: number;
}): string {
  // eslint-disable-next-line no-useless-assignment -- 後続の条件分岐で上書きされるため初期値としてnullを設定
  let startAt = null;
  // eslint-disable-next-line no-useless-assignment -- 後続の条件分岐で上書きされるため初期値としてnullを設定
  let endAt = null;

  // 予約ステータスの場合は、予約日を優先
  if (props.diagnosisStatusDiv == DivConst.items.diagnosisStatus.reserved.value) {
    startAt = props.publishStartAt;
    endAt = props.publishEndAt;
  }
  // その他のステータスの場合は、公開実績を優先
  else {
    startAt = isNotEmpty(props.publishedAt) ? props.publishedAt : props.publishStartAt;

    endAt = isNotEmpty(props.terminatedAt) ? props.terminatedAt : props.publishEndAt;
  }

  if (isEmpty(startAt) && isEmpty(endAt)) {
    return '指定なし';
  }
  if (isEmpty(startAt) && isNotEmpty(endAt)) {
    return `${formatTimeToDate(new Date().getTime(), 'yyyy/MM/dd')} 〜 ${formatTimeToDate(endAt, 'yyyy/MM/dd')}`;
  }
  if (isNotEmpty(startAt) && isEmpty(endAt)) {
    return `${formatTimeToDate(startAt, 'yyyy/MM/dd')} 〜 無期限`;
  }

  return `${formatTimeToDate(startAt, 'yyyy/MM/dd')} 〜 ${formatTimeToDate(endAt, 'yyyy/MM/dd')}`;
}

/**
 * 診断オプションの選択値をidからタイトルに変換
 */
export function convResultListIdToTitle(
  diagnosisResultList: string,
  results: DiagnosisRefResponseResultType[]
): string {
  if (!diagnosisResultList || !results) {
    return '';
  }
  const resultIds = diagnosisResultList.split(',');
  const resultTitles = results
    .filter(result => resultIds.includes(result.id.toString()))
    .map(result => encodeURIComponent(result.title));
  return resultTitles.join(',');
}

/**
 * 診断オプションの選択値をタイトルからidに変換
 */
export function convResultListTitleToId(
  diagnosisResultList: string,
  results: DiagnosisRefResponseResultType[]
): string {
  if (!diagnosisResultList || !results) {
    return '';
  }
  const resultTitles = diagnosisResultList?.split(',')?.map(title => title.trim());
  const resultIds = results
    .filter(result => resultTitles.some(title => result.title.includes(title)))
    .map(result => result.id);
  return resultIds.join(',');
}
